// -----------------------------------------------------------------------------
// core/commands/track-commands.ts — undoable operations on tracks.
//
// Add and remove timeline tracks. Removing a track remembers its position and
// full contents so undo restores it (and any clips on it) exactly.
// (CLAUDE.md §6 Tier 0: "commands for clip+track".)
// -----------------------------------------------------------------------------
import type { Command } from '../commands';
import type { EditorState } from '../store';
import { type ID, type Track, type TrackKind, newId } from '../model';

// Create a new empty track of the given kind, appended (or inserted at index).
export class AddTrackCommand implements Command {
  readonly label = 'Add track';
  private readonly track: Track;
  constructor(kind: TrackKind, name: string, private readonly atIndex?: number) {
    this.track = { id: newId(), kind, name, clips: [] };
  }
  get trackId(): ID {
    return this.track.id;
  }
  do(state: EditorState): void {
    const index = this.atIndex ?? state.project.tracks.length;
    state.project.tracks.splice(index, 0, this.track);
  }
  undo(state: EditorState): void {
    state.project.tracks = state.project.tracks.filter((t) => t.id !== this.track.id);
  }
}

// Remove a track (and everything on it); undo puts it back where it was.
export class RemoveTrackCommand implements Command {
  readonly label = 'Remove track';
  private removed?: { track: Track; index: number };
  private clearedSelection?: ID;
  constructor(private readonly trackId: ID) {}
  do(state: EditorState): void {
    const index = state.project.tracks.findIndex((t) => t.id === this.trackId);
    if (index < 0) return;
    const track = state.project.tracks[index];
    // Snapshot so the restored track (and its clips) is exactly the prior state.
    this.removed = { track: structuredClone(track), index };
    // Clear the selection if it pointed at a clip living on this track.
    if (state.selectedClipId && track.clips.some((c) => c.id === state.selectedClipId)) {
      this.clearedSelection = state.selectedClipId;
      state.selectedClipId = null;
    }
    state.project.tracks.splice(index, 1);
  }
  undo(state: EditorState): void {
    if (!this.removed) return;
    state.project.tracks.splice(this.removed.index, 0, this.removed.track);
    if (this.clearedSelection) {
      state.selectedClipId = this.clearedSelection;
      this.clearedSelection = undefined;
    }
  }
}
